/**
 * @author pkleim on 3/31/16
 *
 * @fileOverview Renders a status message on Login component
 * @module LoginMessage
 * @requires React
 *
 * @param {string} type - 'warning', 'success' or 'neutral'. Default is 'neutral'
 * @example
 * import { Message } from 'one-identity-login';
 * <LoginMessage>Please move on. Nothing to see here.</LoginMessage>
 *
 */
import React from 'react';
import PropTypes from 'prop-types';
import { OnVisible } from '@americanexpress/data-manager-utils';
import {
  PageLevelMessage,
  IconSuccess,
  IconNeutral,
  IconWarning,
} from '@americanexpress/dls-react';
import ExtremeText from '@americanexpress/one-identity-components/lib/components/ExtremeText';

import { MESSAGE_POSITIVE, MESSAGE_WARNING, MESSAGE_NEUTRAL } from '../scripts/constants';

const LoginMessage = ({ type, children }) => {
  let icon = null;

  if (type === MESSAGE_POSITIVE) {
    icon = (
      <IconSuccess
        data-testid="success-icon"
        size="md"
        title="success"
        role="img"
        aria-label="Success"
        theme={{ paddingRight: 0 }}
      />
    );
  }

  if (type === MESSAGE_NEUTRAL) {
    icon = (
      <IconNeutral
        data-testid="info-icon"
        size="md"
        title="Info"
        role="img"
        aria-label="Info"
        theme={{ paddingRight: 0 }}
      />
    );
  }

  if (type === MESSAGE_WARNING) {
    icon = (
      <IconWarning
        data-testid="warning-icon"
        size="md"
        title="Warning"
        role="img"
        aria-label="Warning"
        theme={{ paddingRight: 0 }}
      />
    );
  }

  return (
    <OnVisible analyticsInfo={{ element: 'Login_LoginMessage', detail: children }}>
      <PageLevelMessage
        data-testid="login-message-container"
        icon={icon}
        className="border-0 margin-0"
        type={type}
        dismissible={false}
        filled={true}
      >
        <ExtremeText paragraphs={true} copy={children} />
      </PageLevelMessage>
    </OnVisible>
  );
};

LoginMessage.propTypes = {
  children: PropTypes.string,
  type: PropTypes.oneOf([MESSAGE_NEUTRAL, MESSAGE_POSITIVE, MESSAGE_WARNING]),
};

LoginMessage.defaultProps = {
  children: null,
  type: MESSAGE_NEUTRAL,
};

export default LoginMessage;
